import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import { Plus, Search, X, HandCoins, ArrowDownLeft, ArrowUpRight, Phone, Edit, Trash2, Banknote, Calendar, ChevronLeft, ChevronRight, Clock, CheckCircle, XCircle, User, FileText } from 'lucide-react';

const BusinessTrusts = () => {
  const [trusts, setTrusts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [selectedTrust, setSelectedTrust] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    montant: '', notes: '', date_paiement: new Date().toISOString().split('T')[0]
  });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [formData, setFormData] = useState({
    type: 'recu', personne_nom: '', personne_telephone: '', montant: '', description: '',
    date_depot: new Date().toISOString().split('T')[0], date_echeance: ''
  });

  useEffect(() => { loadTrusts(); }, []);

  const loadTrusts = async () => {
    try {
      const res = await api.get('/business/trusts');
      setTrusts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTrust) {
        await api.put(`/business/trusts/${selectedTrust.id}`, formData);
        toast.success('Dépôt modifié');
      } else {
        await api.post('/business/trusts', formData);
        toast.success('Dépôt enregistré');
      }
      loadTrusts();
      closeModal();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ message: 'Supprimer ce dépôt/prêt ?', type: 'danger', confirmText: 'Supprimer' })) return;
    try {
      await api.delete(`/business/trusts/${id}`);
      toast.success('Supprimé');
      loadTrusts();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const openModal = (trust = null) => {
    if (trust) {
      setSelectedTrust(trust);
      setFormData({
        type: trust.type, personne_nom: trust.personne_nom, personne_telephone: trust.personne_telephone || '',
        montant: trust.montant, description: trust.description || '',
        date_depot: trust.date_depot, date_echeance: trust.date_echeance || ''
      });
    } else {
      setSelectedTrust(null);
      setFormData({
        type: 'recu', personne_nom: '', personne_telephone: '', montant: '', description: '',
        date_depot: new Date().toISOString().split('T')[0], date_echeance: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTrust(null);
  };

  const openPaymentModal = (trust) => {
    setSelectedTrust(trust);
    setPaymentForm({ montant: '', notes: '', date_paiement: new Date().toISOString().split('T')[0] });
    setShowPaymentModal(true);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (paymentProcessing) return;
    setPaymentProcessing(true);
    try {
      await api.post(`/business/trusts/${selectedTrust.id}/payments`, paymentForm);
      toast.success('Remboursement enregistré');
      setShowPaymentModal(false);
      loadTrusts();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du remboursement');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const openPaymentHistory = async (trust) => {
    setSelectedTrust(trust);
    setLoadingPayments(true);
    setShowPaymentHistory(true);
    try {
      const res = await api.get(`/business/trusts/${trust.id}/payments`);
      setPaymentHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPayments(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

  const filtered = trusts.filter(t => {
    const matchSearch = t.personne_nom.toLowerCase().includes(search.toLowerCase()) ||
                        (t.description || '').toLowerCase().includes(search.toLowerCase());
    const matchStatut = !filterStatut || t.statut === filterStatut;
    const matchType = !filterType || t.type === filterType;
    return matchSearch && matchStatut && matchType;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Résumés
  const totalRecu = trusts.filter(t => t.type === 'recu' && t.statut === 'en_cours').reduce((s, t) => s + parseFloat(t.montant_restant || 0), 0);
  const totalConfie = trusts.filter(t => t.type === 'confie' && t.statut === 'en_cours').reduce((s, t) => s + parseFloat(t.montant_restant || 0), 0);
  const enCours = trusts.filter(t => t.statut === 'en_cours').length;
  const rembourses = trusts.filter(t => t.statut === 'rembourse').length;

  const statutBadge = (statut) => {
    const map = {
      en_cours: { label: 'En cours', cls: 'bg-yellow-100 text-yellow-800', icon: <Clock size={12} /> },
      rembourse: { label: 'Remboursé', cls: 'bg-green-100 text-green-800', icon: <CheckCircle size={12} /> },
      annule: { label: 'Annulé', cls: 'bg-red-100 text-red-800', icon: <XCircle size={12} /> }
    };
    const s = map[statut] || map.en_cours;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.icon} {s.label}</span>;
  };

  const typeBadge = (type) => {
    if (type === 'recu') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><ArrowDownLeft size={12} /> Reçu</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"><ArrowUpRight size={12} /> Confié</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <HandCoins className="text-indigo-600" size={24} /> Dépôts & Prêts
          </h1>
          <p className="text-sm text-gray-500 mt-1">Suivi de l'argent confié, indépendant de la caisse</p>
        </div>
        <button onClick={() => openModal()} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm">
          <Plus size={16} /> Nouveau
        </button>
      </div>

      {/* Cartes résumé */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs text-blue-600 font-medium">Argent reçu (en cours)</p>
          <p className="text-lg font-bold text-blue-800">{fmt(totalRecu)} F</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <p className="text-xs text-orange-600 font-medium">Argent confié (en cours)</p>
          <p className="text-lg font-bold text-orange-800">{fmt(totalConfie)} F</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-xs text-yellow-600 font-medium">En cours</p>
          <p className="text-lg font-bold text-yellow-800">{enCours}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-xs text-green-600 font-medium">Remboursés</p>
          <p className="text-lg font-bold text-green-800">{rembourses}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input type="text" placeholder="Rechercher..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
          className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
          <option value="">Tous types</option>
          <option value="recu">Reçu</option>
          <option value="confie">Confié</option>
        </select>
        <select value={filterStatut} onChange={(e) => { setFilterStatut(e.target.value); setCurrentPage(1); }}
          className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
          <option value="">Tous statuts</option>
          <option value="en_cours">En cours</option>
          <option value="rembourse">Remboursé</option>
          <option value="annule">Annulé</option>
        </select>
      </div>

      {/* Liste */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <HandCoins className="mx-auto text-gray-300 mb-3" size={48} />
          <p className="text-gray-500">Aucun dépôt ou prêt enregistré</p>
          <button onClick={() => openModal()} className="mt-3 text-indigo-600 hover:underline text-sm">Ajouter un dépôt</button>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {paginated.map(t => (
              <div key={t.id} className="bg-white rounded-xl border p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-800 flex items-center gap-2"><User size={14} /> {t.personne_nom}</p>
                    {t.personne_telephone && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={12} /> {t.personne_telephone}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {typeBadge(t.type)}
                    {statutBadge(t.statut)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div><span className="text-gray-500">Montant:</span> <span className="font-medium">{fmt(t.montant)} F</span></div>
                  <div><span className="text-gray-500">Restant:</span> <span className="font-medium text-red-600">{fmt(t.montant_restant)} F</span></div>
                  <div className="flex items-center gap-1 text-gray-500"><Calendar size={12} /> {t.date_depot}</div>
                  {t.date_echeance && <div className="text-gray-500 text-xs">Échéance: {t.date_echeance}</div>}
                </div>
                {t.description && <p className="text-xs text-gray-500 mb-3 flex items-start gap-1"><FileText size={12} className="mt-0.5 flex-shrink-0" /> {t.description}</p>}
                <div className="flex gap-2">
                  {t.statut === 'en_cours' && (
                    <button onClick={() => openPaymentModal(t)} className="flex-1 text-xs bg-green-50 text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-100 flex items-center justify-center gap-1">
                      <Banknote size={14} /> Rembourser
                    </button>
                  )}
                  <button onClick={() => openPaymentHistory(t)} className="text-xs bg-gray-50 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-100 flex items-center gap-1">
                    <Clock size={14} /> Historique
                  </button>
                  <button onClick={() => openModal(t)} className="text-xs text-indigo-600 hover:bg-indigo-50 p-1.5 rounded-lg"><Edit size={14} /></button>
                  <button onClick={() => handleDelete(t.id)} className="text-xs text-red-600 hover:bg-red-50 p-1.5 rounded-lg"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Personne</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Montant</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Restant</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Statut</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginated.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{t.personne_nom}</p>
                      {t.personne_telephone && <p className="text-xs text-gray-500">{t.personne_telephone}</p>}
                      {t.description && <p className="text-xs text-gray-400 truncate max-w-[200px]">{t.description}</p>}
                    </td>
                    <td className="px-4 py-3">{typeBadge(t.type)}</td>
                    <td className="px-4 py-3 text-right font-medium">{fmt(t.montant)} F</td>
                    <td className="px-4 py-3 text-right font-medium text-red-600">{fmt(t.montant_restant)} F</td>
                    <td className="px-4 py-3 text-gray-500">
                      <div>{t.date_depot}</div>
                      {t.date_echeance && <div className="text-xs text-orange-500">Éch: {t.date_echeance}</div>}
                    </td>
                    <td className="px-4 py-3">{statutBadge(t.statut)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {t.statut === 'en_cours' && (
                          <button onClick={() => openPaymentModal(t)} className="text-green-600 hover:bg-green-50 p-1.5 rounded-lg" title="Rembourser"><Banknote size={16} /></button>
                        )}
                        <button onClick={() => openPaymentHistory(t)} className="text-gray-500 hover:bg-gray-100 p-1.5 rounded-lg" title="Historique"><Clock size={16} /></button>
                        <button onClick={() => openModal(t)} className="text-indigo-600 hover:bg-indigo-50 p-1.5 rounded-lg" title="Modifier"><Edit size={16} /></button>
                        <button onClick={() => handleDelete(t.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded-lg" title="Supprimer"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">{filtered.length} résultat{filtered.length > 1 ? 's' : ''}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"><ChevronLeft size={16} /></button>
                <span className="text-sm text-gray-600">{currentPage}/{totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">{selectedTrust ? 'Modifier le dépôt' : 'Nouveau dépôt / prêt'}</h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setFormData({...formData, type: 'recu'})}
                    className={`p-3 rounded-lg border text-sm flex items-center gap-2 justify-center transition-colors ${formData.type === 'recu' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'hover:bg-gray-50'}`}>
                    <ArrowDownLeft size={16} /> Argent reçu
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, type: 'confie'})}
                    className={`p-3 rounded-lg border text-sm flex items-center gap-2 justify-center transition-colors ${formData.type === 'confie' ? 'bg-orange-50 border-orange-300 text-orange-700' : 'hover:bg-gray-50'}`}>
                    <ArrowUpRight size={16} /> Argent confié
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {formData.type === 'recu' ? 'Quelqu\'un vous confie son argent' : 'Vous confiez votre argent à quelqu\'un'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la personne *</label>
                <input type="text" required value={formData.personne_nom} onChange={(e) => setFormData({...formData, personne_nom: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Nom complet" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input type="text" value={formData.personne_telephone} onChange={(e) => setFormData({...formData, personne_telephone: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Numéro de téléphone" />
              </div>
              {!selectedTrust && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant * (F CFA)</label>
                  <input type="number" required min="1" value={formData.montant} onChange={(e) => setFormData({...formData, montant: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="0" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date du dépôt *</label>
                  <input type="date" required value={formData.date_depot} onChange={(e) => setFormData({...formData, date_depot: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'échéance</label>
                  <input type="date" value={formData.date_echeance} onChange={(e) => setFormData({...formData, date_echeance: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description / Notes</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" rows={3} placeholder="Détails supplémentaires..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
                  {selectedTrust ? 'Modifier' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal remboursement */}
      {showPaymentModal && selectedTrust && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Remboursement</h2>
              <button onClick={() => setShowPaymentModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4">
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                <p className="font-medium">{selectedTrust.personne_nom}</p>
                <p className="text-gray-500">Restant: <span className="font-semibold text-red-600">{fmt(selectedTrust.montant_restant)} F</span></p>
              </div>
              <form onSubmit={handlePayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant du remboursement *</label>
                  <input type="number" required min="1" max={selectedTrust.montant_restant} value={paymentForm.montant}
                    onChange={(e) => setPaymentForm({...paymentForm, montant: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="date" value={paymentForm.date_paiement} onChange={(e) => setPaymentForm({...paymentForm, date_paiement: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" rows={2} />
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowPaymentModal(false)} className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                  <button type="submit" disabled={paymentProcessing} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
                    {paymentProcessing ? 'Traitement...' : 'Confirmer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal historique remboursements */}
      {showPaymentHistory && selectedTrust && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h2 className="text-lg font-semibold">Historique - {selectedTrust.personne_nom}</h2>
              <button onClick={() => setShowPaymentHistory(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                <div className="flex justify-between">
                  <span>Montant initial</span><span className="font-medium">{fmt(selectedTrust.montant)} F</span>
                </div>
                <div className="flex justify-between">
                  <span>Restant</span><span className="font-medium text-red-600">{fmt(selectedTrust.montant_restant)} F</span>
                </div>
              </div>
              {loadingPayments ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div></div>
              ) : paymentHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">Aucun remboursement enregistré</p>
              ) : (
                <div className="space-y-3">
                  {paymentHistory.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-green-700">+{fmt(p.montant)} F</p>
                        <p className="text-xs text-gray-500">{p.date_paiement}</p>
                        {p.notes && <p className="text-xs text-gray-400 mt-0.5">{p.notes}</p>}
                      </div>
                      <Banknote size={16} className="text-green-400" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessTrusts;
